/**
 * AddChildPopover — the popover that opens when the user clicks the [+]
 * next to a node row in the list-builder.
 *
 * Flow:
 *   1. Ask *what kind of branch*: a new DTMF/next child, a
 *      fallback/timeout child, or *link an existing node*.
 *   2. For a new node: show <NodeTypeQuickPicker /> so the user picks
 *      the type (playback/menu/voicemail/etc.), name it, confirm branch
 *      key, create via POST /ivr-nodes + POST /ivr-edges.
 *   3. For linking: show a searchable list of all other tree nodes,
 *      picking one creates just the edge.
 *
 * Branch-key calculation (menus):
 *   - Standard digits 0-9, plus * and # — set of 12 possible keys.
 *   - A parent's existing edges reserve keys; the popover pre-selects
 *     the lowest unused digit.
 *   - If every digit is used, the popover still allows reassigning by
 *     typing — the backend (not us) validates uniqueness.
 *
 * Branch-key defaults (non-menu):
 *   - playback / bulletin / series_folder → 'next'
 *   - voicemail → 'saved'
 *   - quiz → 'passed'
 *   - payment → 'success'
 *   - conference → 'done'
 *   - anything else → 'next'
 *
 * Special branches:
 *   - 'fallback' / 'timeout' available as one-click presets for menu
 *     parents only. They share the DTMF input field and just set its
 *     value.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ivrNodes, type IvrNodeRow, type IvrNodeType } from '@/api/ivrNodes';
import { ivrEdges } from '@/api/ivrEdges';
import { NodeTypeBadge } from '@/components/ivr/NodeTypeBadge';
import { NodeTypeQuickPicker } from './NodeTypeQuickPicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/cn';
import { formatRef } from '@/lib/formatRef';
import { X, ArrowLeft, Clock, AlertTriangle, Link2, ArrowRight } from 'lucide-react';

// ── Branch-key helpers (exported for tests) ────────────────────────────────────

const MENU_DIGIT_SET: ReadonlyArray<string> = [
  '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '*', '#',
];

/**
 * For a menu-type parent: return the DTMF keys not already used by a
 * child edge. Non-digit branch keys ("fallback"/"timeout"/etc) are not
 * considered part of the DTMF space.
 */
export function availableDtmfKeys(usedKeys: ReadonlyArray<string>): string[] {
  const used = new Set(usedKeys.map((k) => k.trim()));
  return MENU_DIGIT_SET.filter((d) => !used.has(d));
}

/** Default branch-key for a non-menu parent type. */
export function defaultBranchKeyFor(nodeType: IvrNodeType): string {
  switch (nodeType) {
    case 'menu':
    case 'playback_settings':
      return '1';
    case 'playback':
    case 'bulletin':
    case 'series_folder':
    case 'latest_n_folder':
    case 'rotation_folder':
    case 'random_folder':
      return 'next';
    case 'voicemail':
      return 'saved';
    case 'quiz':
      return 'passed';
    case 'payment':
      return 'success';
    case 'conference':
      return 'done';
    case 'survey':
      return 'done';
    case 'enrollment':
      return 'enrolled';
    default:
      return 'next';
  }
}

/** "Menu-like": branch by numeric keypress so a DTMF picker is helpful. */
export function isMenuLike(nodeType: IvrNodeType): boolean {
  return nodeType === 'menu' || nodeType === 'playback_settings';
}

// ── DTMF keypad picker ─────────────────────────────────────────────────────────
// Shows the FULL phone keypad (1-9, 0, *, #) in the familiar layout. Keys
// already used by another branch from this parent are grayed + disabled, so
// the author sees the whole keypad and which keys are taken — and can pick any
// free key, in any order (not just the next one).

const DIALPAD_ROWS: ReadonlyArray<ReadonlyArray<string>> = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
];

function DtmfKeypad({
  value, usedKeys, onPick,
}: {
  value: string;
  usedKeys: ReadonlyArray<string>;
  onPick: (k: string) => void;
}) {
  const used = new Set(usedKeys.map((k) => k.trim()));
  const freeCount = MENU_DIGIT_SET.filter((k) => !used.has(k)).length;
  return (
    <div className="space-y-1.5">
      <div className="grid w-fit grid-cols-3 gap-1.5">
        {DIALPAD_ROWS.flat().map((k) => {
          const isUsed = used.has(k);
          const isSel = value === k;
          return (
            <button
              type="button"
              key={k}
              onClick={() => { if (!isUsed) onPick(k); }}
              disabled={isUsed}
              aria-pressed={isSel}
              title={isUsed ? `Key ${k} is already used by another branch` : `Use key ${k}`}
              className={cn(
                'h-9 w-12 rounded-md border font-mono text-sm font-semibold transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/40',
                isUsed
                  ? 'border-slate-100 bg-slate-50 text-slate-300 line-through cursor-not-allowed'
                  : isSel
                    ? 'border-indigo-500 bg-indigo-600 text-white'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-indigo-50',
              )}
            >
              {k}
            </button>
          );
        })}
      </div>
      <p className="text-[10px] text-slate-400">
        {freeCount > 0
          ? `${freeCount} free · grayed keys are already used by another branch`
          : 'All keys are in use — remove a branch to free one.'}
      </p>
    </div>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────

type Step = 'mode' | 'type' | 'link';

interface Props {
  /** The parent node we're adding a child to. */
  parent: IvrNodeRow;
  /**
   * All edges outgoing from the parent (so we can compute the unused
   * DTMF keys for a menu).
   */
  parentOutgoingEdges: ReadonlyArray<{ branch_key: string; to_node_id: string | null }>;
  /** All nodes in the tree (for "link existing"). */
  treeNodes: ReadonlyArray<IvrNodeRow>;
  treeId: string;
  onClose: () => void;
  /** Fired after a successful create — parent re-fetches. */
  onCreated?: (childId: string) => void;
}

export function AddChildPopover({
  parent, parentOutgoingEdges, treeNodes, treeId,
  onClose, onCreated,
}: Props) {
  const qc = useQueryClient();

  const menuLike = isMenuLike(parent.node_type);
  const availableKeys = useMemo(
    () => availableDtmfKeys(parentOutgoingEdges.map((e) => e.branch_key)),
    [parentOutgoingEdges],
  );
  // The DTMF keys already taken by a sibling branch — passed to the keypad so
  // it can gray them out (rather than hide them).
  const usedDtmfKeys = useMemo(
    () =>
      parentOutgoingEdges
        .map((e) => e.branch_key.trim())
        .filter((k) => MENU_DIGIT_SET.includes(k)),
    [parentOutgoingEdges],
  );
  const usedFallback = parentOutgoingEdges.some((e) => e.branch_key === 'fallback');
  const usedTimeout = parentOutgoingEdges.some((e) => e.branch_key === 'timeout');

  // Step state — start on the "mode" screen.
  const [step, setStep] = useState<Step>('mode');

  // Selection state carried across steps.
  const [kind, setKind] = useState<'dtmf' | 'fallback' | 'timeout' | 'next' | 'link' | null>(null);
  const [branchKey, setBranchKey] = useState<string>('');
  const [nodeType, setNodeType] = useState<IvrNodeType>('playback');
  const [name, setName] = useState('');
  const [linkQuery, setLinkQuery] = useState('');
  const [linkTargetId, setLinkTargetId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const nameInputRef = useRef<HTMLInputElement>(null);

  // ── Close on Esc ──────────────────────────────────────────────────────────
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ── Focus name when entering type step ────────────────────────────────────
  useEffect(() => {
    if (step === 'type') {
      setTimeout(() => nameInputRef.current?.focus(), 30);
    }
  }, [step]);

  // ── Mutations ─────────────────────────────────────────────────────────────

  const createChild = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error('Name required');
      if (!branchKey.trim()) throw new Error('Branch key required');

      const newNode = await ivrNodes.create(treeId, {
        node_type: nodeType,
        name: name.trim(),
        config: {},
      });
      await ivrEdges.create(parent.id, {
        branch_key: branchKey.trim(),
        to_node_id: newNode.id,
      });
      return newNode;
    },
    onSuccess: (node) => {
      qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrEdgesAll', treeId] });
      onCreated?.(node.id);
      onClose();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Create failed'),
  });

  const linkExisting = useMutation({
    mutationFn: async () => {
      if (!linkTargetId) throw new Error('Pick a node');
      if (!branchKey.trim()) throw new Error('Branch key required');
      await ivrEdges.create(parent.id, {
        branch_key: branchKey.trim(),
        to_node_id: linkTargetId,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrEdgesAll', treeId] });
      if (linkTargetId) onCreated?.(linkTargetId);
      onClose();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Link failed'),
  });

  // ── Step transitions ──────────────────────────────────────────────────────

  function pickKind(k: 'dtmf' | 'fallback' | 'timeout' | 'next' | 'link') {
    setKind(k);
    setError(null);
    if (k === 'dtmf') {
      setBranchKey(availableKeys[0] ?? '1');
      setStep('type');
    } else if (k === 'fallback') {
      setBranchKey('fallback');
      setStep('type');
    } else if (k === 'timeout') {
      setBranchKey('timeout');
      setStep('type');
    } else if (k === 'next') {
      setBranchKey(defaultBranchKeyFor(parent.node_type));
      setStep('type');
    } else if (k === 'link') {
      setBranchKey(
        menuLike ? (availableKeys[0] ?? '1') : defaultBranchKeyFor(parent.node_type),
      );
      setStep('link');
    }
  }

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-label={`Add child under ${parent.name}`}
        className={cn(
          'fixed z-50 max-w-[calc(100vw-2rem)] max-h-[85vh] flex flex-col rounded-lg border border-slate-200 bg-white shadow-xl overflow-hidden',
          // Mode step is a short list — compact width, natural height. The
          // type/link steps switch to a two-column layout (controls rail +
          // full-height options grid) so nothing nests two scrollbars and
          // the Create button is always visible without scrolling.
          step === 'mode' ? 'w-[460px]' : 'w-[720px] h-[min(85vh,580px)]',
        )}
        style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2.5">
          {step !== 'mode' && (
            <button
              type="button"
              onClick={() => { setStep('mode'); setError(null); }}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              aria-label="Back"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
              Add child under
            </p>
            <p className="text-xs font-semibold text-slate-800 truncate">
              <span className="font-mono text-slate-500 text-[11px] mr-1">
                {formatRef(parent.ref_number)}
              </span>
              {parent.name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
            aria-label="Close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Body — mode scrolls as one block; type/link manage their own
            two-column layout with a single scroll region. */}
        <div
          className={cn(
            'flex-1 min-h-0',
            step === 'mode' ? 'overflow-y-auto px-3 py-3' : 'overflow-hidden max-sm:overflow-y-auto',
          )}
        >
          {step === 'mode' && (
            <ModeStep
              parent={parent}
              availableKeys={availableKeys}
              usedFallback={usedFallback}
              usedTimeout={usedTimeout}
              menuLike={menuLike}
              onPick={pickKind}
            />
          )}

          {step === 'type' && (
            <TypeStep
              parent={parent}
              kind={kind!}
              usedKeys={usedDtmfKeys}
              branchKey={branchKey}
              nodeType={nodeType}
              name={name}
              error={error}
              pending={createChild.isPending}
              nameInputRef={nameInputRef}
              onBranchKeyChange={setBranchKey}
              onNodeTypeChange={setNodeType}
              onNameChange={setName}
              onSubmit={() => { setError(null); createChild.mutate(); }}
            />
          )}

          {step === 'link' && (
            <LinkStep
              parent={parent}
              treeNodes={treeNodes}
              query={linkQuery}
              branchKey={branchKey}
              targetId={linkTargetId}
              error={error}
              pending={linkExisting.isPending}
              menuLike={menuLike}
              usedKeys={usedDtmfKeys}
              onQueryChange={setLinkQuery}
              onBranchKeyChange={setBranchKey}
              onTargetChange={setLinkTargetId}
              onSubmit={() => { setError(null); linkExisting.mutate(); }}
            />
          )}
        </div>
      </div>
    </>
  );
}

// ── Step: choose mode ────────────────────────────────────────────────────────

function ModeStep({
  parent, availableKeys, usedFallback, usedTimeout, menuLike, onPick,
}: {
  parent: IvrNodeRow;
  availableKeys: string[];
  usedFallback: boolean;
  usedTimeout: boolean;
  menuLike: boolean;
  onPick: (k: 'dtmf' | 'fallback' | 'timeout' | 'next' | 'link') => void;
}) {
  const availCount = availableKeys.length;

  return (
    <div className="space-y-1.5">
      {menuLike ? (
        <>
          <ModeTile
            title="New child via DTMF key"
            hint={
              availCount > 0
                ? `Next free digit: ${availableKeys[0]} · ${availCount} unused`
                : 'All 12 keys are in use — picker will still let you pick one to overwrite'
            }
            badge={availableKeys[0] ?? '—'}
            onClick={() => onPick('dtmf')}
          />
          <ModeTile
            title="Fallback child"
            hint={usedFallback ? 'Already has a fallback child' : 'Runs when input is invalid'}
            icon={<AlertTriangle className="h-3.5 w-3.5" />}
            disabled={usedFallback}
            onClick={() => onPick('fallback')}
          />
          <ModeTile
            title="Timeout child"
            hint={usedTimeout ? 'Already has a timeout child' : 'Runs when caller doesn\u2019t press in time'}
            icon={<Clock className="h-3.5 w-3.5" />}
            disabled={usedTimeout}
            onClick={() => onPick('timeout')}
          />
        </>
      ) : (
        <ModeTile
          title="Next node"
          hint={`Linked as \u201C${defaultBranchKeyFor(parent.node_type)}\u201D from this ${parent.node_type}`}
          icon={<ArrowRight className="h-3.5 w-3.5" />}
          onClick={() => onPick('next')}
        />
      )}

      <div className="pt-1.5 pb-1">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 px-0.5">
          Advanced
        </p>
      </div>

      <ModeTile
        title="Link existing node"
        hint="Reuse a node already in this tree (e.g. shared voicemail box)"
        icon={<Link2 className="h-3.5 w-3.5" />}
        onClick={() => onPick('link')}
      />
    </div>
  );
}

function ModeTile({
  title, hint, badge, icon, disabled, onClick,
}: {
  title: string;
  hint: string;
  badge?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'group w-full text-left rounded-md border px-3 py-2 transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-indigo-400/40',
        disabled
          ? 'border-slate-100 bg-slate-50/40 opacity-50 cursor-not-allowed'
          : 'border-slate-200 bg-white hover:bg-indigo-50/40 hover:border-indigo-200',
      )}
    >
      <div className="flex items-center gap-2">
        {icon && <span className="shrink-0 text-indigo-500">{icon}</span>}
        <span className="text-[13px] font-medium text-slate-800 truncate flex-1">
          {title}
        </span>
        {badge !== undefined && (
          <span className="shrink-0 inline-flex items-center justify-center rounded border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-indigo-700 leading-none">
            {badge}
          </span>
        )}
      </div>
      <p className="mt-0.5 text-[11px] text-slate-500 leading-snug">{hint}</p>
    </button>
  );
}

// ── Step: pick type + name + branch key ──────────────────────────────────────

function TypeStep({
  parent, kind, usedKeys, branchKey, nodeType, name, error, pending,
  nameInputRef,
  onBranchKeyChange, onNodeTypeChange, onNameChange, onSubmit,
}: {
  parent: IvrNodeRow;
  kind: 'dtmf' | 'fallback' | 'timeout' | 'next' | 'link';
  usedKeys: ReadonlyArray<string>;
  branchKey: string;
  nodeType: IvrNodeType;
  name: string;
  error: string | null;
  pending: boolean;
  nameInputRef: React.MutableRefObject<HTMLInputElement | null>;
  onBranchKeyChange: (v: string) => void;
  onNodeTypeChange: (v: IvrNodeType) => void;
  onNameChange: (v: string) => void;
  onSubmit: () => void;
}) {
  const keyLocked = kind === 'fallback' || kind === 'timeout';

  return (
    <form
      className="flex h-full min-h-0 max-sm:flex-col max-sm:h-auto"
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
    >
      {/* Left rail \u2014 key + name + Create, always visible (no scrolling
          needed to reach the button). */}
      <div className="w-[240px] shrink-0 flex flex-col gap-3 border-e border-slate-100 p-3 overflow-y-auto max-sm:w-full max-sm:border-e-0 max-sm:border-b max-sm:overflow-visible">
        <div className="space-y-1">
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Branch key
          </Label>
          {kind === 'dtmf' ? (
            <DtmfKeypad value={branchKey} usedKeys={usedKeys} onPick={onBranchKeyChange} />
          ) : (
            <Input
              value={branchKey}
              onChange={(e) => onBranchKeyChange(e.target.value)}
              className="h-8 font-mono text-sm"
              disabled={keyLocked}
              maxLength={32}
            />
          )}
          <p className="text-[10px] text-slate-400">
            How this edge is reached from{' '}
            <span className="font-mono">{formatRef(parent.ref_number)}</span>.
          </p>
        </div>

        <div className="space-y-1">
          <Label
            htmlFor="acp-name"
            className="text-[11px] font-semibold uppercase tracking-wider text-slate-400"
          >
            Name
          </Label>
          <Input
            ref={nameInputRef}
            id="acp-name"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="e.g. Hours playback"
            className="h-8 text-sm"
            maxLength={200}
          />
        </div>

        {error && (
          <p role="alert" className="text-[11px] text-red-500">{error}</p>
        )}

        <div className="mt-auto pt-1">
          <Button
            type="submit"
            size="sm"
            className="w-full h-8 text-xs"
            disabled={!name.trim() || !branchKey.trim() || pending}
          >
            {pending ? 'Creating\u2026' : 'Create & link'}
          </Button>
        </div>
      </div>

      {/* Right \u2014 node-type options fill the dialog height; the tile grid
          is the single scroll region. */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col p-3 max-sm:min-h-[320px]">
        <Label className="pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          Node type
        </Label>
        <NodeTypeQuickPicker
          fillHeight
          className="flex-1 min-h-0"
          value={nodeType}
          onPick={onNodeTypeChange}
        />
      </div>
    </form>
  );
}

// ── Step: link existing node ──────────────────────────────────────────────────

function LinkStep({
  parent, treeNodes, query, branchKey, targetId, error, pending,
  menuLike, usedKeys,
  onQueryChange, onBranchKeyChange, onTargetChange, onSubmit,
}: {
  parent: IvrNodeRow;
  treeNodes: ReadonlyArray<IvrNodeRow>;
  query: string;
  branchKey: string;
  targetId: string | null;
  error: string | null;
  pending: boolean;
  menuLike: boolean;
  usedKeys: ReadonlyArray<string>;
  onQueryChange: (v: string) => void;
  onBranchKeyChange: (v: string) => void;
  onTargetChange: (id: string) => void;
  onSubmit: () => void;
}) {
  const candidates = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const others = treeNodes.filter((n) => n.id !== parent.id && !n.deleted_at);
    if (!needle) return others.slice(0, 40);
    return others
      .filter((n) =>
        n.name.toLowerCase().includes(needle) ||
        n.ref_number.toLowerCase().includes(needle) ||
        n.node_type.toLowerCase().includes(needle))
      .slice(0, 40);
  }, [treeNodes, query, parent.id]);

  return (
    <form
      className="flex h-full min-h-0 max-sm:flex-col max-sm:h-auto"
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
    >
      {/* Left rail \u2014 branch key + Link button, always visible. */}
      <div className="w-[240px] shrink-0 flex flex-col gap-3 border-e border-slate-100 p-3 overflow-y-auto max-sm:w-full max-sm:border-e-0 max-sm:border-b max-sm:overflow-visible">
        <div className="space-y-1">
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Branch key
          </Label>
          {menuLike ? (
            <DtmfKeypad value={branchKey} usedKeys={usedKeys} onPick={onBranchKeyChange} />
          ) : (
            <Input
              value={branchKey}
              onChange={(e) => onBranchKeyChange(e.target.value)}
              className="h-8 font-mono text-sm"
              maxLength={32}
            />
          )}
        </div>

        {error && (
          <p role="alert" className="text-[11px] text-red-500">{error}</p>
        )}

        <div className="mt-auto pt-1">
          <Button
            type="submit"
            size="sm"
            className="w-full h-8 text-xs"
            disabled={!targetId || !branchKey.trim() || pending}
          >
            {pending ? 'Linking\u2026' : 'Link as child'}
          </Button>
        </div>
      </div>

      {/* Right \u2014 search + candidate list; the list is the single scroll
          region and fills the dialog height. */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col gap-2 p-3 max-sm:min-h-[320px]">
        <div className="space-y-1">
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Target node
          </Label>
          <Input
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search by name, ref, or type\u2026"
            className="h-8 text-sm"
          />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto rounded-md border border-slate-200 bg-white divide-y divide-slate-100">
          {candidates.length === 0 ? (
            <div className="px-3 py-4 text-[11px] text-slate-400 italic text-center">
              No nodes match.
            </div>
          ) : (
            candidates.map((n) => {
              const active = n.id === targetId;
              return (
                <button
                  type="button"
                  key={n.id}
                  onClick={() => onTargetChange(n.id)}
                  className={cn(
                    'w-full text-left px-2.5 py-1.5 flex items-center gap-2 text-sm',
                    'hover:bg-indigo-50/40 transition-colors',
                    active && 'bg-indigo-50 ring-1 ring-indigo-200',
                  )}
                  aria-pressed={active}
                >
                  <span className="font-mono text-[10px] text-slate-500 shrink-0">
                    {formatRef(n.ref_number)}
                  </span>
                  <span className="truncate text-slate-800">{n.name}</span>
                  <span className="ms-auto shrink-0">
                    <NodeTypeBadge type={n.node_type} />
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>
    </form>
  );
}
